import * as Yup from "yup";

import { ABNValidator } from "lib/validation";
import { PICValidator } from "lib/validation/validators";
import { parsePicValues } from "constants/sideLoadedBusinesses";
import { pluralize } from "lib/pluralize";
import { parseSideloadedTransitInsuranceValue } from "lib/insuranceRates";
import { AUSTRALIAN_STATES } from "constants/address";
import {
  getAusPaymentsNetworkBsbs,
  normaliseBsb,
  BSBSixDigit,
  BSBEntry,
} from "constants/ausPaymentsNetworkBsbs";
import {
  LIVESTOCK_AGENT_CONTENT_TYPE_NAME,
  SALEYARD_ADMIN_CONTENT_TYPE_NAME,
} from "constants/users";

export const DEFAULT_SIDE_LOADED_USER_ROLES = [
  LIVESTOCK_AGENT_CONTENT_TYPE_NAME,
  SALEYARD_ADMIN_CONTENT_TYPE_NAME,
];

export const NOTES_FIELD_INDICES = [1, 2, 3, 4, 5] as const;

export const PIC_FIELD_INDICES = [1, 2, 3, 4, 5] as const;

export enum FieldGroup {
  General = "General",
  Financial = "Financial",
  Properties = "Properties",
  Contacts = "Contacts",
  Commissions = "Commissions",
  Notes = "Notes",
}

export interface FieldConfiguration {
  value: string; // Reference that needs to be unique here.
  targetFieldName?: string; // The field name on the business form.
  label: string; // A human readable label for the field.
  validation?: Yup.AnySchema | null; // A Yup Validation function that will be used to validate the field.
  sourceFieldSuggestions: string[]; // A list of field names from a CSV that will trigger this field as a default value in the source data.
  group: FieldGroup; // The heading the field is displayed under in the compare modal.
  handleAsProperty?: boolean; // PICs gather several columns, split to a property each.
  handleAsContact?: boolean; // Contacts tie their indexed fields into one recipient.
  index?: number; // A number value that indicates the index when used with contacts.
  fieldName?: string; // A string value that indicates the field name on the business form.   Falls back to value in most cases.
  isBusinessLookup?: boolean; // Whether this field is a business lookup.
  handleAsNotes?: boolean; // Notes allow multiple mappings and are additive to data, rather than replacing it.
  handleAsLabels?: boolean; // Labels are a comma separated cell resolved to Business Labels, and are additive.
  /** Which user roles see this row in the compare modal. */
  forUserRoles: string[];
}

const BSB_LOOKUP = getAusPaymentsNetworkBsbs().reduce(
  (acc, bsbEntry) => {
    acc[bsbEntry[0]] = bsbEntry;
    return acc;
  },
  {} as Record<BSBSixDigit, BSBEntry>,
);

function checkIsRegistered(value: string | null): boolean {
  return !value || Boolean(BSB_LOOKUP[normaliseBsb(value)]);
}

export const bsbValidation = Yup.string()
  .nullable()
  .test("is-registered", "That BSB is not recognised", checkIsRegistered);

export const accountNumberValidation = Yup.string().nullable();

export const accountNameValidation = Yup.string().nullable();

export const nameValidation = Yup.string()
  .label("Name")
  .max(100, "${path} can not be longer than 100 characters");

export const publicDisplayNameValidation = Yup.string()
  .label("Public Display Name")
  .max(200)
  .nullable();

export const shortCodeValidation = Yup.string()
  .label("Agent Account Reference")
  .max(16)
  .nullable();

export const shortCodeSaleyardValidation = Yup.string()
  .label("Saleyard Account Reference")
  .max(16)
  .nullable();

export const shortCodeAuctionsPlusValidation = Yup.string()
  .label("Auctions Plus Shortcode")
  .max(50)
  .nullable();

export const addressingInformationValidation = Yup.string()
  .label("Contact/Place Name")
  .max(120, "${path} can not be longer than 120 characters");

export const streetNumberValidation = Yup.string()
  .label("Street Number")
  .max(20, "${path} can not be longer than 20 characters");

export const routeValidation = Yup.string()
  .label("Route")
  .max(100, "${path} can not be longer than 100 characters");

export const localityValidation = Yup.string()
  .label("Locality")
  .max(165, "${path} can not be longer than 165 characters");

export const strictStateValidation = Yup.string()
  .label("State")
  .nullable()
  .oneOf(
    [...AUSTRALIAN_STATES, "", null],
    `\${path} must be one of ${AUSTRALIAN_STATES.join(", ")}`,
  );

export const strictPostalCodeValidation = Yup.string()
  .label("Postal Code")
  .nullable()
  .matches(/^\d{4}$/, {
    message: "${path} must be 4 digits",
    excludeEmptyString: true,
  });

const contactConfigurations = [1, 2, 3, 4, 5].reduce<
  Record<string, FieldConfiguration>
>((acc, index) => {
  acc[`contact_email_${index}`] = {
    value: `contact_email_${index}`,
    label: `Contact Email ${index}`,
    targetFieldName: `emailRecipients[${index - 1}].email`,
    sourceFieldSuggestions: [`email${index}`].concat(
      index === 1 ? ["email"] : [],
    ),
    index,
    fieldName: "email",
    handleAsContact: true,
    group: FieldGroup.Contacts,
    validation: Yup.string().email("Invalid email"),
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  };
  acc[`contact_phone_${index}`] = {
    value: `contact_phone_${index}`,
    label: `Contact Phone ${index}`,
    targetFieldName: `emailRecipients[${index - 1}].phoneNumber`,
    sourceFieldSuggestions: [`phone${index}`].concat(
      index === 1 ? ["phone"] : [],
    ),
    index,
    fieldName: "phoneNumber",
    handleAsContact: true,
    group: FieldGroup.Contacts,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  };
  acc[`contact_first_name_${index}`] = {
    value: `contact_first_name_${index}`,
    label: `Contact First Name ${index}`,
    targetFieldName: `emailRecipients[${index - 1}].firstName`,
    sourceFieldSuggestions: [`firstname${index}`].concat(
      index === 1 ? ["firstname"] : [],
    ),
    index,
    fieldName: "firstName",
    handleAsContact: true,
    group: FieldGroup.Contacts,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  };
  acc[`contact_last_name_${index}`] = {
    value: `contact_last_name_${index}`,
    label: `Contact Last Name ${index}`,
    targetFieldName: `emailRecipients[${index - 1}].lastName`,
    sourceFieldSuggestions: [`lastname${index}`].concat(
      index === 1 ? ["lastname"] : [],
    ),
    index,
    fieldName: "lastName",
    handleAsContact: true,
    group: FieldGroup.Contacts,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  };
  return acc;
}, {});

const PIC_SOURCE_FIELD_SUGGESTIONS = [
  "pic",
  "pics",
  "piccode",
  "piccodes",
  ...PIC_FIELD_INDICES.map(index => `pic${index}`),
];

export const picListValidation = Yup.string().test({
  name: "pics",
  test(value) {
    const invalid = parsePicValues([value]).filter(
      pic => !PICValidator.isValidSync(pic),
    );
    if (!invalid.length) {
      return true;
    }
    return this.createError({
      message: `${pluralize("Invalid PIC", invalid.length)}: ${invalid.join(", ")}`,
    });
  },
});

export const fieldConfigurations: Record<string, FieldConfiguration> = {
  // Simple "General Collapse" fields.
  NAME: {
    value: "name",
    targetFieldName: "name",
    label: "Name",
    validation: nameValidation.required("${path} is required"),
    sourceFieldSuggestions: ["name", "displayname", "tradingname"],
    group: FieldGroup.General,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  PUBLIC_DISPLAY_NAME: {
    value: "publicDisplayName",
    targetFieldName: "publicDisplayName",
    label: "Public Display Name",
    validation: publicDisplayNameValidation,
    sourceFieldSuggestions: [],
    group: FieldGroup.General,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  SHORT_CODE: {
    value: "shortCode",
    targetFieldName: "shortCode",
    label: "Agent Reference",
    validation: shortCodeValidation,
    sourceFieldSuggestions: ["agentreference", "code", "agentcode", "agentref"],
    group: FieldGroup.General,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  SHORT_CODE_SALEYARD: {
    value: "shortCodeSaleyard",
    targetFieldName: "shortCodeSaleyard",
    label: "Saleyard Reference",
    validation: shortCodeSaleyardValidation,
    sourceFieldSuggestions: [
      "saleyardreference",
      "saleyardcode",
      "syref",
      "sycode",
    ],
    group: FieldGroup.General,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  SHORT_CODE_AUCTIONS_PLUS: {
    value: "shortCodeAuctionsPlus",
    targetFieldName: "shortCodeAuctionsPlus",
    label: "Auctions Plus Shortcode",
    validation: shortCodeAuctionsPlusValidation,
    sourceFieldSuggestions: ["auctionsplusshortcode", "auctionsplus"],
    group: FieldGroup.General,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  BRANDS: {
    value: "brands",
    targetFieldName: "brands",
    label: "Brands",
    validation: null,
    sourceFieldSuggestions: ["brand", "brands"],
    group: FieldGroup.General,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  LABELS: {
    value: "labels",
    targetFieldName: "labels",
    label: "Labels",
    validation: null,
    sourceFieldSuggestions: ["labels", "label", "tags", "tag"],
    group: FieldGroup.General,
    handleAsLabels: true,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  ADDRESS_ADRESSING_INFORMATION: {
    value: "addressAddressingInformation",
    label: "Address Addressing Information",
    targetFieldName: "address.addressingInformation",
    validation: addressingInformationValidation,
    sourceFieldSuggestions: ["address1"],
    group: FieldGroup.General,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  ADDRESS_STREET_NUMBER: {
    value: "addressStreetNumber",
    label: "Address Street Number",
    targetFieldName: "address.streetNumber",
    validation: streetNumberValidation,
    sourceFieldSuggestions: ["streetnumber", "addressstreetnumber"],
    group: FieldGroup.General,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  ADDRESS_ROUTE: {
    value: "addressRoute",
    label: "Address Street Name",
    targetFieldName: "address.route",
    validation: routeValidation,
    sourceFieldSuggestions: ["addressroute", "streetname", "street"],
    group: FieldGroup.General,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  ADDRESS_LOCALITY: {
    value: "addressLocality",
    label: "Address City",
    targetFieldName: "address.locality",
    validation: localityValidation,
    sourceFieldSuggestions: ["locality", "town", "city", "suburb", "address2"],
    group: FieldGroup.General,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  ADDRESS_STATE: {
    value: "addressState",
    label: "Address State",
    targetFieldName: "address.state",
    validation: strictStateValidation,
    sourceFieldSuggestions: ["addressstate", "state", "statename"],
    group: FieldGroup.General,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  ADDRESS_POSTAL_CODE: {
    value: "addressPostalCode",
    label: "Address Post Code",
    targetFieldName: "address.postalCode",
    validation: strictPostalCodeValidation,
    sourceFieldSuggestions: ["postcode", "postalcode", "physicalpostcode"],
    group: FieldGroup.General,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  // Financial fields.
  BSB: {
    value: "bankingDetailsBsb",
    label: "BSB",
    targetFieldName: "bankingDetails.bsb",
    validation: bsbValidation,
    sourceFieldSuggestions: ["bsb", "bsbnumber"],
    group: FieldGroup.Financial,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  ACCOUNT_NUMBER: {
    value: "bankingDetailsAccountNumber",
    label: "Account Number",
    targetFieldName: "bankingDetails.accountNumber",
    validation: accountNumberValidation,
    sourceFieldSuggestions: ["accountnumber", "account"],
    group: FieldGroup.Financial,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  ACCOUNT_NAME: {
    value: "bankingDetailsAccountName",
    label: "Account Name",
    targetFieldName: "bankingDetails.accountName",
    validation: accountNameValidation,
    sourceFieldSuggestions: ["accountname", "accname"],
    group: FieldGroup.Financial,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  ABN: {
    value: "abn",
    label: "ABN",
    targetFieldName: "abn",
    sourceFieldSuggestions: ["abn"],
    validation: ABNValidator,
    group: FieldGroup.Financial,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  TRANSIT_INSURANCE: {
    value: "transitInsurance",
    targetFieldName: "__transitInsurance",
    label: "Transit Insurance",
    validation: Yup.string().test({
      name: "validTransitInsuranceValue",
      message: "Transit Insurance must be Buy, Sell, Buy/Sell, or empty.",
      test: value =>
        value == null ||
        value === "" ||
        parseSideloadedTransitInsuranceValue(value) !== undefined,
    }),
    sourceFieldSuggestions: [
      "transitinsurance",
      "transitinsuranceapplication",
      "transitinsurancebuysell",
    ],
    group: FieldGroup.Financial,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  PICS: {
    value: "pics",
    targetFieldName: "properties",
    label: "PICs",
    validation: picListValidation,
    sourceFieldSuggestions: PIC_SOURCE_FIELD_SUGGESTIONS,
    group: FieldGroup.Properties,
    handleAsProperty: true,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  // Enumerated - Contacts
  ...contactConfigurations,

  // Agency Commissions
  VENDOR_PERCENTAGE_COMMISSION: {
    value: "vendorPercentageCommission",
    label: "Agency Commission Override",
    targetFieldName: "vendorPercentageCommission",
    sourceFieldSuggestions: ["comm", "comms"],
    validation: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        typeof originalValue === "string" && originalValue.trim() === ""
          ? null
          : value,
      )
      .typeError("${path} must be a number")
      .min(0)
      .max(100),
    group: FieldGroup.Commissions,
    forUserRoles: [LIVESTOCK_AGENT_CONTENT_TYPE_NAME],
  },

  // - Stock Agent
  STOCK_AGENT: {
    value: "stockAgent",
    label: "Stock Agent",
    targetFieldName: "stockAgent",
    sourceFieldSuggestions: ["agent", "stockagent"],
    validation: null, // This is handled upstream in the form declaration.
    isBusinessLookup: true,
    group: FieldGroup.Commissions,
    forUserRoles: [LIVESTOCK_AGENT_CONTENT_TYPE_NAME],
  },

  STOCK_AGENT_PERCENTAGE_COMMISSION: {
    value: "stockAgentPercentageCommission",
    label: "Stock Agent Percentage Commission",
    targetFieldName: "stockAgentPercentageCommission",
    sourceFieldSuggestions: ["agentcommissions"],
    validation: null, // This is handled upstream in the form declaration.
    group: FieldGroup.Commissions,
    forUserRoles: [LIVESTOCK_AGENT_CONTENT_TYPE_NAME],
  },

  // Currently a single entry - consider if we need multiple?
  REBATE_AGENT: {
    value: "rebateAgent",
    label: "Rebate Agent",
    targetFieldName: "rebateAgent",
    sourceFieldSuggestions: ["rebagent", "rebateagent"],
    validation: null, // This is handled upstream in the form declaration.
    isBusinessLookup: true,
    group: FieldGroup.Commissions,
    forUserRoles: [LIVESTOCK_AGENT_CONTENT_TYPE_NAME],
  },
  REBATE_AGENT_PERCENTAGE_COMMISSION: {
    value: "rebateAgentPercentageCommission",
    label: "Rebate Agent Percentage Commission",
    targetFieldName: "rebateAgentPercentageCommission",
    sourceFieldSuggestions: [
      "impliedcommissions",
      "calculatedrebatecommission",
    ],
    validation: null, // This is handled upstream in the form declaration.
    group: FieldGroup.Commissions,
    forUserRoles: [LIVESTOCK_AGENT_CONTENT_TYPE_NAME],
  },

  NOTES: {
    value: "notes",
    targetFieldName: "notes",
    label: "Notes",
    validation: null,
    sourceFieldSuggestions: [
      "note",
      "notes",
      "extra",
      "more",
      "commissionnotes",
    ],
    group: FieldGroup.Notes,
    handleAsNotes: true,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
};

export interface FieldConfigurationGroup {
  group: FieldGroup;
  configurations: FieldConfiguration[];
}

export function groupConfigurationsByGroup(
  configurations: FieldConfiguration[],
): FieldConfigurationGroup[] {
  const groups: FieldConfigurationGroup[] = [];
  configurations.forEach(configuration => {
    const { group } = configuration;
    const currentGroup = groups[groups.length - 1];
    if (!currentGroup || currentGroup.group !== group) {
      groups.push({ group, configurations: [configuration] });
    } else {
      currentGroup.configurations.push(configuration);
    }
  });
  return groups;
}

export function getSideLoadedFieldConfigurationsForUser(
  userRole: string | null,
): FieldConfiguration[] {
  if (userRole === null) {
    return [];
  }
  return Object.values(fieldConfigurations).filter(configuration =>
    configuration.forUserRoles.includes(userRole),
  );
}
